<script setup>
import {onMounted, ref} from 'vue'
import {useForm} from "laravel-precognition-vue-inertia";

import Button from 'primevue/button'
import Tag from 'primevue/tag'
import SelectButton from "primevue/selectbutton";
import FileUploader from "../../components/ui-components/FileUploader.vue";
import FormInput from "../../../widget/components/ui-components/FormInput.vue";
import Select from "primevue/select";
import Avatar from "primevue/avatar";
import WorkersSelector from "../../components/ui-components/WorkersSelector.vue";
import axios from "axios";
import {Link} from "@inertiajs/vue3";
import { trans } from 'laravel-vue-i18n';

const props = defineProps({
    service: Object,
    workers: Array,
    forms: Object,
})

const form = useForm('patch', '/app/services/' + (props.service ? props.service?.id : 'new'), {
    id: props.service?.id || 'new',
    name: props.service?.name || '',
    description: props.service?.description || '',
    currency: props.service?.currency || 'CZK',
    price: props.service?.price || null,
    min_price: props.service?.min_price || null,
    max_price: props.service?.max_price || null,
    unit: props.service?.unit || '',
    image: props.service?.image || '',
    length_in_minutes: props.service?.length_in_minutes || 30,
    occupy_by: props.service?.occupy_by || 'service_length',
    featured: props.service?.featured === 1,
    service_workers: props.service?.service_workers || [],
    sort: props.service?.sort || 0,
    price_preference: !props.service?.max_price ? 'fix' : 'range',
    has_calendar_api_key: !!props.forms?.calendar_api_key,
    id_reservation_forms: props.forms?.id
})

const currencies = [
    { label: trans('services.currency_czk'), value: 'CZK' },
]
const occupyOptions = [
    { label: trans('services.occupy_by_service'), value: 'service_length' },
    { label: trans('services.occupy_by_slot'),    value: 'slot_length' },
]

const calendars = ref([])

const loadingCalendars = ref(false);
const getCalendars = () => {
    const provider = props.forms.provider?.name;
    loadingCalendars.value = true;
    axios.get(`/app/forms/available_calendars/${provider}`).then((response) => {
        calendars.value = Object.values(response.data);
        loadingCalendars.value = false;
    })
}

onMounted(() => {
    getCalendars();
})


const priceOptions = [
    { label: trans('services.price_fixed'), value: 'fix' },
    { label: trans('services.price_range'), value: 'range' },
];
</script>

<template>
    <div class="flex flex-col lg:flex-row gap-8 text-gray-800 dark:text-gray-200">
        <div class="flex-1 space-y-8">
            <div
                class="bg-white/80 dark:bg-slate-800/60 border dark:border-neutral-700 rounded-xl shadow-sm backdrop-blur-md w-full">
                <div class="p-6 border-b dark:border-neutral-700 flex gap-4">
                    <i class="pi pi-briefcase text-indigo-500 !text-xl"></i>
                    <div>
                        <p class="font-bold">{{ $t('services.section_basic') }}</p>
                        <p class="text-sm">{{ $t('services.section_basic_desc') }}</p>
                    </div>
                </div>
                <div class="p-6 space-y-4">
                    <form-input type="text" v-model="form.name" :label="$t('services.field_name')" required
                                :invalid="form.errors['name']"></form-input>
                    <form-input type="textarea" v-model="form.description" :label="$t('services.field_description')"
                                :invalid="form.errors['description']"></form-input>
                    <div>
                        <label class="font-semibold text-sm mb-1">{{ $t('services.field_image') }}</label>
                        <FileUploader v-model:src="form.image"></FileUploader>
                    </div>
                </div>
            </div>


            <div
                class="bg-white/80 dark:bg-slate-800/60 border dark:border-neutral-700 rounded-xl shadow-sm backdrop-blur-md">
                <div class="p-6 border-b dark:border-neutral-700 flex gap-4">
                    <i class="pi pi-users text-teal-500 !text-xl"></i>
                    <div>
                        <p class="font-bold">{{ $t('common.colleagues') }}</p>
                        <p class="text-sm">{{ $t('services.section_colleagues_desc') }}</p>
                    </div>
                </div>

                <div class=" space-y-3">
                    <div v-if="form.service_workers.length === 0"
                         class="text-sm opacity-60 p-5 border-b dark:border-neutral-700 bg-neutral-100">
                        {{ $t('services.no_workers_long') }}
                    </div>

                    <div class=" ">
                        <div class="text-sm border-b-0" :class="{
                                'grid grid-cols-2': form.service_workers.length > 8
                            }">
                            <div v-for="(worker, i) in form.service_workers" :key="worker.id" :class="{
                                    'border m-1 rounded-xl': form.service_workers.length > 8,
                                    'border-b': form.service_workers.length <= 8
                                }">
                                <div
                                    class="flex dark:border-gray-700 p-5 "
                                    :class="!forms?.calendar_api_key ? 'pb-2' : ''">
                                    <div class="flex gap-3 w-1/4 items-center grow"
                                         :class="form.service_workers.length > 8 ? 'grow':''">
                                        <Avatar
                                            :label="$page.props.workers.find((el) => el.id === worker.id_reservation_workers)?.initials"></Avatar>
                                        <p class="font-bold">
                                            {{
                                                $page.props.workers.find((el) => el.id === worker.id_reservation_workers)?.name
                                            }}</p>
                                    </div>

                                    <div class="flex gap-2">
                                        <Link :href="`/app/workers/${worker.id_reservation_workers}`">
                                            <Button text severity="secondary"
                                                    icon="pi pi-pencil"
                                                    v-tooltip.bottom="$t('services.tooltip_edit_worker', { name: $page.props.workers.find((el) => el.id === worker.id_reservation_workers)?.name })"></Button>
                                        </Link>
                                        <Button text severity="danger" @click="form.service_workers.splice(i, 1)"
                                                icon="pi pi-eject"
                                                v-tooltip.bottom="$t('services.tooltip_disconnect_worker', { name: $page.props.workers.find((el) => el.id === worker.id_reservation_workers)?.name })"></Button>
                                    </div>
                                </div>
                                <div v-if="!forms?.calendar_api_key" class="flex flex-col justify-center m-5 mt-0"
                                     :class="form.service_workers.length > 8 ? 'w-1/2':'grow'">
                                    <Select :options="calendars" :invalid="form.errors[`service_workers.${i}.calendar_api_key`]" :loading="loadingCalendars"
                                            v-model="worker.calendar_api_key" option-label="name" option-value="id">
                                        <template #value="slotProps">
                                            <div v-if="slotProps.value" class="flex items-center gap-3">
                                                <i class="pi pi-calendar"
                                                   :style="{ color: calendars.find(el => el.id === slotProps.value)?.color }"/>
                                                <div>{{ calendars.find(el => el.id === slotProps.value)?.name }}</div>
                                            </div>
                                            <div v-else>
                                                <span class="opacity-50">{{ $t('services.calendar_for', { name: $page.props.workers.find((el) => el.id === worker.id_reservation_workers)?.name }) }}</span>
                                            </div>
                                        </template>
                                        <template #option="slotProps">
                                            <div class="flex items-center gap-3">
                                                <i class="pi pi-calendar" :style="{ color: slotProps.option.color }"/>
                                                <div>{{ slotProps.option.name }}</div>
                                            </div>
                                        </template>

                                    </Select>
                                </div>
                                <div v-if="form.errors[`service_workers.${i}.calendar_api_key`]" class="text-red-500 w-fit m-5 !-mt-4 text-sm">{{form.errors[`service_workers.${i}.calendar_api_key`]}}</div>
                            </div>
                        </div>
                        <workers-selector class="m-2" :workers="$page.props.workers"
                                          :skip-workers="form.service_workers.map((el) => el.id_reservation_workers)"
                                          slim
                                          @select="(worker) => { form.service_workers.push({
                                                id_reservation_workers: worker.id,
                                                id_reservation_services: service?.id
                                              }); }"></workers-selector>
                    </div>
                </div>

            </div>
            <div v-if="form.errors['service_workers']" class="text-red-500 bg-red-100 p-1 border border-red-500 rounded w-fit !mt-2 text-sm">{{form.errors['service_workers']}}</div>


            <p v-if="forms?.calendar_api_key" class="text-center opacity-60 text-xs">
                {{ $t('services.shared_calendar') }} <span class="border p-2 py-1 rounded"
                                                            :style="`border-color: ${calendars.find(el => forms?.calendar_api_key === el.id)?.color};color: ${calendars.find(el => forms.calendar_api_key === el.id)?.color}`">{{
                    calendars.find(el => forms?.calendar_api_key === el.id)?.name
                }}</span>
            </p>
            <p v-if="!forms?.calendar_api_key" class="text-center opacity-60 text-xs">
                {{ $t('services.per_worker_calendar') }}
            </p>
        </div>


        <!-- PRAVÝ PANEL -->
        <aside class="w-full lg:w-80  flex flex-col max-md:flex-col-reverse gap-6">

            <Button :label="$t('services.btn_save')" fluid :loading="form.processing"
                    @click="form.submit"/>

            <div
                v-if="props.service"
                class="bg-white/80 dark:bg-slate-800/60 border dark:border-neutral-700 rounded-xl shadow-sm backdrop-blur-md p-6 text-sm">
                <p><strong>{{ $t('services.meta_created') }}</strong> {{ new Date(props.service?.created_at).toLocaleString() }}</p>
                <p><strong>{{ $t('services.meta_updated') }}</strong> {{ new Date(props.service?.updated_at).toLocaleString() }}</p>
            </div>
            <div
                class="bg-white/80 dark:bg-slate-800/60 border dark:border-neutral-700 rounded-xl shadow-sm backdrop-blur-md w-full">
                <div class="p-6 border-b dark:border-neutral-700 flex gap-4">
                    <i class="pi pi-wallet text-green-500 !text-xl"></i>
                    <div>
                        <p class="font-bold">{{ $t('services.section_prices') }}</p>
                        <p class="text-sm">{{ $t('services.section_prices_desc') }}</p>
                    </div>
                </div>
                <div class="p-6 space-y-4">
                    <SelectButton v-model="form.price_preference" option-label="label" option-value="value"
                                  :options="priceOptions"></SelectButton>
                    <form-input v-if="form.price_preference === 'fix'" type="price" :currency="form.currency"
                                v-model="form.price" :label="$t('services.field_price')" required :invalid="form.errors['price']"></form-input>

                    <div v-else class="grid grid-cols-1 gap-4">
                        <form-input type="price" :currency="form.currency" v-model="form.min_price"
                                    :label="$t('services.field_min_price')" required :invalid="form.errors['min_price']"></form-input>
                        <form-input type="price" :currency="form.currency" v-model="form.max_price"
                                    :label="$t('services.field_max_price')" required :invalid="form.errors['max_price']"></form-input>
                    </div>

                    <div class="grid grid-cols-1 gap-4">
                        <form-input type="select" :options="currencies" v-model="form.currency" :label="$t('services.field_currency')"
                                    :invalid="form.errors['currency']"></form-input>
                    </div>
                </div>
            </div>
            <div
                class="bg-white/80 dark:bg-slate-800/60 border dark:border-neutral-700 rounded-xl shadow-sm backdrop-blur-md">
                <div class="p-6 border-b dark:border-neutral-700 flex gap-4">
                    <i class="pi pi-cog text-orange-500 !text-xl"></i>
                    <div>
                        <p class="font-bold">{{ $t('services.section_settings') }}</p>
                        <p class="text-sm">{{ $t('services.section_settings_desc') }}</p>
                    </div>
                </div>

                <div class="p-6 space-y-6">
                    <form-input type="number" v-model="form.length_in_minutes" :label="$t('services.field_length')" required
                                :invalid="form.errors['length_in_minutes']"></form-input>
                    <form-input type="select" :options="occupyOptions" v-model="form.occupy_by"
                                :label="$t('services.field_occupy_by')" :invalid="form.errors['occupy_by']"></form-input>
                    <small class="opacity-65">
                        <template v-if="form.occupy_by === 'service_length'">
                            {{ $t('services.event_length_service', { minutes: form.length_in_minutes }) }}
                        </template>
                        <template v-else>
                            {{ $t('services.event_length_slot') }}
                        </template>
                    </small>
                </div>
            </div>


            <!-- METADATA -->
        </aside>

    </div>
</template>
